import { StyledComponentProps } from "@mui/styles";
import { FeedClassKey } from "assets/jss/common-styles";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiContainer from "components/MuiExtras/MuiContainer";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import HtmlUtils from "lib/HtmlUtils";
import { FeedType, PublicationType, SystemRoles } from "models/ApiEnums";
import { FeedItemDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RoutePaths } from "routes/RouteConfig";
import { CardTopMessageMode } from "views/Timeline/Components/CardLib/CardSetup";
import { CardBodyData } from "views/Timeline/Components/CardLib/CardBodyData";
import InlineTopicEditDrawer from "views/Timeline/Dialogs/InlineTopicEditDrawer";
import { TopicLinkTo } from "views/Topic/Components/TopicLinkTo";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    dialog: any;
}

interface IProps {
    data: FeedItemDto;
    feedType: FeedType;
    fullView: boolean;
    inApprovalMode: boolean;
    mouseOver: boolean;
    textExpanded: boolean;
    topMessageMode: CardTopMessageMode;
    onUpdateCard?: (data: DeepPartial<FeedItemDto>) => void;
}

type Props = IProps & StyledComponentProps<FeedClassKey> & WithSnackbar & WithTranslation;

type TopMessageType = {
    message: JSX.Element | string;
    className: string;
    action?: () => void;
};
/////////////////////////////////////////////////////////////////////////////////////


class HeaderLine extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
        };
    }

    ///
    private feedStartTopMessages(): TopMessageType {
        const { t, data: { interaction, action } } = this.props;

        // Está aprovando: é recomendação?
        if (interaction.wasRecommended)
            return { message: t("Common.Feed.Literals.RecommendedForYou"), className: null };

        // Está aprovando: é uma pergunta para você responder?
        if (action && action.forYourAnswer)
            return { message: t("Common.Feed.Literals.RepassedForYourAnswer"), className: null };

        return null;
    }

    ///
    private feedCourseTopMessages(): TopMessageType {
        const { t, textExpanded, data: { interaction }, data } = this.props;

        if (interaction.courseEnroll.expired) {
            // Curso Expirado
            if (interaction.courseItem.completedOn == null)
                return { message: t("Common.Feed.Course.HeaderItemNotFinished"), className: this.props.classes.messageBlockWarning };
        }
        else if (interaction.courseEnroll.completedOn != null) {
            // Curso Completado
            if (interaction.courseItem.completedOn == null && interaction.courseItem.createdOn > interaction.courseEnroll.completedOn)
                return { message: t("Common.Feed.Course.HeaderItemAddAfterEnrollFinished"), className: this.props.classes.messageBlockWarning };
        }
        else if (interaction.courseItem.completedOn == null) {
            // Item em andamento
            const cardBody = new CardBodyData(data, false, false);
            return interaction.courseItem.containVideo || interaction.courseItem.containEmbedVideo
                ? { message: t("Common.Feed.Course.HeaderItemWillAutoFinishVideo"), className: this.props.classes.messageBlockWarning }
                : (textExpanded === false)
                    ? { message: t("Common.Feed.Course.HeaderItemViewToComplete"), className: this.props.classes.messageBlockWarning }
                    :  cardBody.hasLink()
                        ? { message: t("Common.Feed.Course.HeaderItemWillAutoFinishLink"), className: this.props.classes.messageBlockWarning }
                        : cardBody.hasFile()
                            ? { message: t("Common.Feed.Course.HeaderItemWillAutoFinishFile"), className: this.props.classes.messageBlockWarning }
                            : { message: t("Common.Feed.Course.HeaderItemManualFinish"), className: this.props.classes.messageBlockWarning };
        }
        return null;
    }

    ///
    private createTopMessage(): TopMessageType {
        const { data: { publication, publicationType }, feedType, inApprovalMode, topMessageMode } = this.props;

        // 1.
        if (feedType == FeedType.CourseItems)
            return null;

        // 2.
        if (feedType == FeedType.CourseViewer)
            return this.feedCourseTopMessages();

        // 3.
        if (feedType == FeedType.Start && !inApprovalMode && topMessageMode == "default") {
            const result = this.feedStartTopMessages();
            if (result != null) return result;
        }

        // 4.
        if (feedType != FeedType.Topic && publicationType != PublicationType.Answer && publication.topics.length > 0) {
            return { message: <TopicLinkTo id={publication.topics[0].key} name={publication.topics[0].label} />, className: this.props.classes.titleContainer };
        }

        // 5.
        if (feedType == FeedType.MyDraft && publicationType == PublicationType.Answer && publication.topics.length > 0) {
            return { message: <TopicLinkTo id={publication.topics[0].key} name={publication.topics[0].label} />, className: this.props.classes.titleContainer };
        }

        return null;
    }

    ///
    private renderTopMessage(topData: TopMessageType) {
        if (topData != null) {
            if (topData.action == null)
                return (<span key={0} className={topData.className}>{topData.message}</span>);
            else
                return (<MuiClickable key={0} onClick={topData.action}><span className={topData.className}>{topData.message}</span></MuiClickable>);
        }
        return null;
    }

    ///
    render() {
        const { classes, feedType, fullView, mouseOver } = this.props;
        const { dialog } = this.state;
        const headerLine: React.ReactNode[] = [];

        if (fullView) {
            headerLine.push(
                <InlineTopicEditDrawer
                    classes={classes}
                    data={this.props.data}
                    skipTopicAccessCheckFor={
                        feedType == FeedType.CourseItems || feedType == FeedType.CourseViewer
                            ? SystemRoles.CourseManager
                            : feedType != FeedType.CourseAutoEnroll && feedType != FeedType.CourseEnrolled && feedType != FeedType.CourseCompleted
                                ? SystemRoles.PublicationManager
                                : null
                    }
                    mouseOver={mouseOver}
                    onTopicClick={(id) => HtmlUtils.openUrlInNewWindow(RoutePaths.TopicViewer.replace(":id", id))}
                    onUpdateCard={(item) => this.props.onUpdateCard && this.props.onUpdateCard(item)}
                />
            );
        }
        else {
            const topData = this.createTopMessage();
            if (topData != null) headerLine.push(this.renderTopMessage(topData));
        }

        return headerLine.length > 0
            ? (
                <MuiContainer margin="dense">
                    {dialog}
                    {headerLine}
                </MuiContainer>
            )
            : null;
    }
}

export default withSnackbar(withTranslation()(HeaderLine));
